/*
 * Hamilton Sinkler Custom Wishlist Utilities
 * Description: Shared formatting, pricing, tear-sheet, timing, DOM, escaping, and download helpers.
 * Dependencies: hs-wishlist-core.js
 * Public Methods: debounce, throttle, formatMoney, getApprovedPricingRule, getApprovedPrice, buildTearSheetUrl, qs, qsa, escapeHTML, toBoolean, downloadBlob, createElement
 * Private Methods: normalizeCents, formatWithDelimiters, formatShopifyMoney, normalizeTags, sanitizeTearSheetFilename
 * Version: 1.2.0
 */
(function (window, document) {
  'use strict';
  var HS = window.HSWishlist;

  function normalizeCents(value) {
    var number = Number(value || 0);
    return Number.isFinite(number) ? number : 0;
  }

  function normalizeTags(tags) {
    return (tags || []).map(function (tag) { return String(tag || '').toLowerCase(); });
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var amount = normalizeCents(cents) / 100;
    var fixed = amount.toFixed(precision);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return precision > 0 ? parts[0] + decimal + parts[1] : parts[0];
  }

  function formatShopifyMoney(cents, format) {
    var nextFormat = format || '${{amount}}';
    var placeholder = nextFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    var value;
    switch (placeholder && placeholder[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return nextFormat.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]+>/g, '');
  }

  function sanitizeTearSheetFilename(title) {
    return String(title || '').replace(/\s+/g, '_').replace(/[()]/g, '') + '.pdf';
  }

  function debounce(fn, wait) {
    var timeout;
    return function debounced() {
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function throttle(fn, wait) {
    var last = 0;
    return function throttled() {
      var now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn.apply(null, arguments);
      }
    };
  }

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(normalizeCents(cents), format || '${{amount}}').replace(/<[^>]+>/g, '');
    if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') return window.theme.Currency.formatMoney(normalizeCents(cents), format || '${{amount}}').replace(/<[^>]+>/g, '');
    return formatShopifyMoney(cents, format);
  }

  function getApprovedPricingRule(customer) {
    if (!window.bmtConfig || !window.bmtConfig.rules) return null;
    var configTags = normalizeTags(window.bmtConfig.customer && window.bmtConfig.customer.tags);
    var customerTags = normalizeTags((customer && customer.tags && customer.tags.length) ? customer.tags : configTags);
    return window.bmtConfig.rules.find(function (rule) {
      return rule && rule.status === 'active' && (rule.customerTags || []).some(function (tag) { return customerTags.indexOf(String(tag || '').toLowerCase()) > -1; });
    }) || null;
  }

  function getApprovedPrice(cents, customer) {
    var original = normalizeCents(cents), rule = getApprovedPricingRule(customer), discount = parseFloat(rule && rule.discountValue);
    if (!rule || !discount || discount <= 0) return { original: original, final: original, discount: 0, approved: false };
    return { original: original, final: Math.round(original * ((100 - discount) / 100)), discount: discount, approved: true };
  }

  function buildTearSheetUrl(title, template) {
    if (!template) return '';
    var filename = sanitizeTearSheetFilename(title);
    var url = String(template).replace('HS_WISHLIST_FILENAME.pdf', encodeURIComponent(filename).replace(/%2F/g, '/'));
    return url.indexOf('//') === 0 ? 'https:' + url : url;
  }

  function qs(selector, root) { return (root || document).querySelector(selector); }
  function qsa(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }
  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>'"]/g, function (char) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char];
    });
  }
  function toBoolean(value) { return value === true || value === 'true'; }
  function downloadBlob(blob, filename) {
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }
  function createElement(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  }

  HS.define('utils', { debounce: debounce, throttle: throttle, formatMoney: formatMoney, getApprovedPricingRule: getApprovedPricingRule, getApprovedPrice: getApprovedPrice, buildTearSheetUrl: buildTearSheetUrl, qs: qs, qsa: qsa, escapeHTML: escapeHTML, toBoolean: toBoolean, downloadBlob: downloadBlob, createElement: createElement });
})(window, document);

